import { SolutionMetadata } from '@ardoq/core';
import {
  CreateReportFromLocation,
  DrilldownFromLocations,
  OpenReportBuilderFromLocation,
  ReportEventLocations,
  ReportExportType,
  ReportReaderLocation,
  ReportTrackingEvents,
  TrackEvent,
} from './types';
import {
  Aggregate,
  APIReportFilters,
  ArdoqId,
  DataSourceType,
} from '@ardoq/api-types';

export const getTrackingFunctionsForContext = (
  trackEvent: TrackEvent,
  from: ReportReaderLocation
) => ({
  trackSorting: () =>
    trackEvent(ReportTrackingEvents.SORTED_REPORT_READER, { from }),
  trackFiltering: () =>
    trackEvent(ReportTrackingEvents.FILTERED_REPORT_READER, { from }),
  trackMissingReadAccess: () =>
    trackEvent(ReportTrackingEvents.REPORT_READER_USER_MISSING_READ_ACCESS, {
      from,
    }),
});

export const trackExportReport = (
  trackEvent: TrackEvent,
  from: ReportReaderLocation,
  exportType: ReportExportType
) => trackEvent(ReportTrackingEvents.EXPORTED_REPORT, { from, exportType });

export const trackCopyReportURL = (
  trackEvent: TrackEvent,
  from:
    | ReportReaderLocation
    | ReportEventLocations.REPORT_OVERVIEW
    | ReportEventLocations.ASSET_MANAGER,
  copyURLType: ReportReaderLocation,
  hasFiltersApplied: boolean = false
) =>
  trackEvent(ReportTrackingEvents.COPIED_REPORT_URL, {
    from,
    copyURLType,
    hasFiltersApplied,
  });

export const trackCreateReport = (
  trackEvent: TrackEvent,
  from: CreateReportFromLocation
) => trackEvent(ReportTrackingEvents.CREATED_REPORT, { from });

export const trackOpenReportBuilder = (
  trackEvent: TrackEvent,
  from: OpenReportBuilderFromLocation,
  solutionMetadata: SolutionMetadata,
  reportId?: ArdoqId
) =>
  trackEvent(ReportTrackingEvents.OPENED_REPORT_BUILDER, {
    from,
    ...(reportId ? { resourceId: reportId } : {}),
    ...solutionMetadata,
  });

export const trackOpenComponentInWorkspace = (
  trackEvent: TrackEvent,
  from: ReportReaderLocation
) =>
  trackEvent(ReportTrackingEvents.OPENED_COMPONENT_FROM_REPORT_READER, {
    from,
  });

export const trackOpenComponentOverviewPage = (
  trackEvent: TrackEvent,
  from: ReportReaderLocation
) =>
  trackEvent(
    ReportTrackingEvents.OPENED_COMPONENT_OVERVIEW_FROM_REPORT_READER,
    {
      from,
    }
  );

type SaveReportMetadata = {
  dataSource: DataSourceType;
  numberOfColumnsRenamed: number;
  numberOfColumnsSelected: number;
  numberOfWorkspacesSelected: number;
  defaultAggregate: Aggregate | null;
  hasDefaultSort: boolean;
  isNew: boolean;
} & SolutionMetadata;
export const trackSaveReport = (
  trackEvent: TrackEvent,
  metadata: SaveReportMetadata
) => trackEvent(ReportTrackingEvents.SAVED_REPORT, metadata);

export const trackOpenReportReader = (
  trackEvent: TrackEvent,
  from: ReportReaderLocation,
  reportId: ArdoqId,
  solutionMetadata: SolutionMetadata
) =>
  trackEvent(ReportTrackingEvents.OPENED_REPORT_READER, {
    from,
    resourceId: reportId,
    ...solutionMetadata,
  });

export const trackDrilldown = (
  trackEvent: TrackEvent,
  from: DrilldownFromLocations,
  widgetType:
    | 'pieChart'
    | 'table'
    | 'number'
    | 'trend'
    | 'lineChart'
    | 'stackedBarChart',
  filters?: APIReportFilters
) =>
  trackEvent(ReportTrackingEvents.DRILLED_DOWN_REPORT_READER, {
    from,
    widgetType,
    hasFilters: Boolean(filters),
  });

export const trackQueryResults = (
  trackEvent: TrackEvent,
  queryType: DataSourceType,
  from:
    | ReportEventLocations.REPORT_BUILDER
    | ReportEventLocations.AD_HOC_ADVANCED_SEARCH
    | ReportEventLocations.AD_HOC_GREMLIN_SEARCH,
  numberOfResults: number
) =>
  trackEvent(ReportTrackingEvents.QUERIED_RESULTS, {
    queryType,
    from,
    numberOfResults,
  });

export const trackExportAdHocSearchToExcel = (
  trackEvent: TrackEvent,
  datasource: DataSourceType
) => trackEvent(ReportTrackingEvents.EXPORTED_AD_HOC_TO_EXCEL, { datasource });
